import { FC, useEffect, useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faIdCard } from '@fortawesome/free-solid-svg-icons';
import { apiGetRandomPodium, type RandomPodiumResponse, type LeaderboardEntry } from '../../api/userCardApi';
import { apiGetCurrentQuiz } from '../../api/quizApi';
import { useAppSelector } from '../../store/hooks';

// ─── Styled ───────────────────────────────────────────────────────────────────

const Wrapper = styled.div<{ $isMobile?: boolean }>`
  width: ${({ $isMobile }) => $isMobile ? '100%' : '90%'};
  margin: ${({ $isMobile }) => $isMobile ? '0 0 16px' : '0 0 20px'};
  background: linear-gradient(160deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.28);
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.3);
  }
`;

const Header = styled.div<{ $bgImage?: string; $isMobile?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: ${({ $isMobile }) => $isMobile ? '6px 12px' : '11px 18px'};
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  background-color: rgba(255, 255, 255, 0.04);

  ${({ $bgImage }) => $bgImage && `
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: url('${$bgImage}') center / cover no-repeat;
      opacity: 0.2;
      pointer-events: none;
    }
  `}

  & > * {
    position: relative;
  }
`;

const CardIcon = styled.span`
  color: rgba(255, 215, 0, 0.6);
  font-size: 13px;
  flex-shrink: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const HeaderTitle = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.13em;
`;

const ExpansionBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 215, 0, 0.75);
  background: rgba(255, 215, 0, 0.08);
  border: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: 4px;
  padding: 1px 7px;
  letter-spacing: 0.05em;
`;

const RandomNote = styled.span`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.65);
  font-style: italic;
  margin-left: auto;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.8);
`;

const Body = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  flex-direction: ${({ $isMobile }) => $isMobile ? 'column' : 'row'};
  align-items: stretch;
  justify-content: center;
  padding: ${({ $isMobile }) => $isMobile ? '10px 14px' : '14px 18px'};
  gap: ${({ $isMobile }) => $isMobile ? '0' : '12px'};
`;

const Divider = styled.div<{ $isMobile?: boolean }>`
  ${({ $isMobile }) => $isMobile
    ? 'width: 100%; height: 1px;'
    : 'width: 1px; height: auto;'
  }
  background: rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
`;

// ─── Mini claim button ────────────────────────────────────────────────────────

const miniPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(76,175,111,0.4); }
  50%       { box-shadow: 0 0 0 5px rgba(76,175,111,0); }
`;

const MiniQuizBtn = styled.button<{ $active: boolean }>`
  padding: 3px 10px;
  border-radius: 5px;
  border: 1px solid ${({ $active }) => ($active ? '#38bdf8' : 'rgba(255,255,255,0.12)')};
  background-color: ${({ $active }) => ($active ? 'rgba(56,189,248,0.15)' : 'rgba(255,255,255,0.04)')};
  color: ${({ $active }) => ($active ? '#38bdf8' : 'rgba(255,255,255,0.3)')};
  font-size: 10px;
  font-weight: 700;
  cursor: ${({ $active }) => ($active ? 'pointer' : 'default')};
  white-space: nowrap;
  flex-shrink: 0;
  letter-spacing: 0.03em;
  transition: background-color 0.15s;

  &:hover {
    background-color: ${({ $active }) => ($active ? 'rgba(56,189,248,0.25)' : 'rgba(255,255,255,0.04)')};
  }
`;

const MiniClaimBtn = styled.button<{ $active: boolean }>`
  padding: 3px 10px;
  border-radius: 5px;
  border: 1px solid ${({ $active }) => ($active ? '#4caf6f' : 'rgba(255,255,255,0.12)')};
  background-color: ${({ $active }) => ($active ? 'rgba(76,175,111,0.15)' : 'rgba(255,255,255,0.04)')};
  color: ${({ $active }) => ($active ? '#4caf6f' : 'rgba(255,255,255,0.3)')};
  font-size: 10px;
  font-weight: 700;
  cursor: ${({ $active }) => ($active ? 'pointer' : 'default')};
  white-space: nowrap;
  flex-shrink: 0;
  animation: ${({ $active }) => ($active ? miniPulse : 'none')} 2s infinite;
  transition: background-color 0.15s;
  letter-spacing: 0.03em;

  &:hover {
    background-color: ${({ $active }) => ($active ? 'rgba(76,175,111,0.25)' : 'rgba(255,255,255,0.04)')};
  }
`;

// ─── Loading ──────────────────────────────────────────────────────────────────

const shimmer = keyframes`
  0%   { opacity: 0.4; }
  50%  { opacity: 0.15; }
  100% { opacity: 0.4; }
`;

const LoadingBody = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 18px;
  width: 100%;
`;

const SkeletonBlock = styled.div<{ $w?: string; $h?: string; $circle?: boolean }>`
  width: ${({ $w }) => $w ?? '100%'};
  height: ${({ $h }) => $h ?? '14px'};
  border-radius: ${({ $circle }) => ($circle ? '50%' : '6px')};
  background: rgba(255, 255, 255, 0.1);
  animation: ${shimmer} 1.4s ease-in-out infinite;
  flex-shrink: 0;
`;

const SkeletonEntry = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

// ─── Rank colors ──────────────────────────────────────────────────────────────

const RANK_COLOR: Record<number, string> = {
  1: 'gold',
  2: '#c0c0c0',
  3: '#cd7f32',
};

const PodiumEntry = styled.div<{ $rank: number }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  padding: 8px 14px;
  border-radius: 7px;
  background: ${({ $rank }) => $rank === 1 ? 'rgba(255, 215, 0, 0.04)' : 'transparent'};
`;

const RankBadge = styled.div<{ $rank: number }>`
  font-size: 20px;
  font-weight: 800;
  color: ${({ $rank }) => RANK_COLOR[$rank] ?? 'rgba(255,255,255,0.25)'};
  flex-shrink: 0;
  width: 20px;
  text-align: center;
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
  flex-shrink: 0;
`;

const Username = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.88);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.1;
`;

const Nickname = styled.span`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.38);
  font-style: italic;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.1;
`;

const WrestlerAvatars = styled.div`
  display: flex;
  flex-direction: row;
  gap: 3px;
  flex-shrink: 0;
`;

const WrestlerAvatar = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  object-fit: cover;
  object-position: top;
  border: 1px solid rgba(163, 27, 27, 0.35);
`;

const ScoreBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  margin-left: auto;
`;

const Score = styled.span<{ $rank: number }>`
  font-size: 16px;
  font-weight: 800;
  color: ${({ $rank }) => RANK_COLOR[$rank] ?? 'rgba(255,255,255,0.4)'};
`;

const ScoreLabel = styled.span`
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.2);
  margin-top: -2px;
`;

// ─── Sub-component ────────────────────────────────────────────────────────────

const PodiumEntryItem: FC<{ entry: LeaderboardEntry }> = ({ entry }) => (
  <PodiumEntry $rank={entry.rank}>
    <RankBadge $rank={entry.rank}>{entry.rank}</RankBadge>
    <TextBlock>
      <Username>{entry.username}</Username>
      {entry.nickname_forum && <Nickname>"{entry.nickname_forum}"</Nickname>}
    </TextBlock>
    {entry.wrestlers.length > 0 && (
      <WrestlerAvatars>
        {entry.wrestlers.slice(0, 3).map((w) =>
          w.avatar_path ? (
            <WrestlerAvatar
              key={w.id}
              src={process.env.REACT_APP_STORAGE_URL + w.avatar_path}
              alt={w.name}
              title={w.name}
            />
          ) : null
        )}
      </WrestlerAvatars>
    )}
    <ScoreBlock>
      <Score $rank={entry.rank}>{entry.score}</Score>
      <ScoreLabel>punti</ScoreLabel>
    </ScoreBlock>
  </PodiumEntry>
);

// ─── Main component ───────────────────────────────────────────────────────────

const getCountdown = (nextClaimAt: string): string => {
  const diff = new Date(nextClaimAt).getTime() - Date.now();
  if (diff <= 0) return '';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const CardPodium: FC<{ isMobile?: boolean }> = ({ isMobile }) => {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user ?? state.auth.userLoggedState);
  const cardAccount = user?.card_account ?? null;

  const [data, setData] = useState<RandomPodiumResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [canClaim, setCanClaim] = useState(cardAccount?.can_claim ?? false);
  const [nextClaimAt, setNextClaimAt] = useState<string | null>(cardAccount?.next_claim_at ?? null);
  const [countdown, setCountdown] = useState('');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [quizAvailable, setQuizAvailable] = useState(false);
  const [quizNextAt, setQuizNextAt] = useState<string | null>(null);
  const [quizCountdown, setQuizCountdown] = useState('');
  const quizIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (cardAccount) {
      setCanClaim(cardAccount.can_claim);
      setNextClaimAt(cardAccount.next_claim_at);
    }
  }, [cardAccount]);

  useEffect(() => {
    if (!nextClaimAt) { setCountdown(''); return; }
    const tick = () => {
      const c = getCountdown(nextClaimAt);
      if (!c) { setCanClaim(true); setNextClaimAt(null); setCountdown(''); }
      else setCountdown(c);
    };
    tick();
    intervalRef.current = setInterval(tick, 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [nextClaimAt]);

  useEffect(() => {
    let active = true;
    apiGetRandomPodium()
      .then((res) => { if (active) setData(res); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!user) return;
    apiGetCurrentQuiz()
      .then((res) => {
        if (res.status === 'available') {
          setQuizAvailable(true);
        } else {
          setQuizAvailable(false);
          setQuizNextAt(res.available_at);
        }
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!quizNextAt) { setQuizCountdown(''); return; }
    const tick = () => {
      const c = getCountdown(quizNextAt);
      if (!c) { setQuizAvailable(true); setQuizNextAt(null); setQuizCountdown(''); }
      else setQuizCountdown(c);
    };
    tick();
    quizIntervalRef.current = setInterval(tick, 1000);
    return () => { if (quizIntervalRef.current) clearInterval(quizIntervalRef.current); };
  }, [quizNextAt]);

  if (!loading && (!data || data.podium.length === 0)) return null;

  const label = data?.type === 'expansion' && data.expansion
    ? data.expansion.name
    : 'Globale';

  const bgImage = data?.type === 'expansion' && data.expansion?.expansion_path
    ? process.env.REACT_APP_STORAGE_URL + data.expansion.expansion_path
    : undefined;

  const miniClaimLabel = canClaim
    ? 'Riscatta ora'
    : countdown
      ? `Tra ${countdown}`
      : null;

  return (
    <Wrapper $isMobile={isMobile} onClick={() => navigate('/carte')}>
      <Header $bgImage={bgImage} $isMobile={isMobile}>
        <HeaderLeft>
          <CardIcon><FontAwesomeIcon icon={faIdCard} /></CardIcon>
          <HeaderTitle>GWF Cards — Top 3</HeaderTitle>
          {!loading && !isMobile && <ExpansionBadge>{label}</ExpansionBadge>}
        </HeaderLeft>
        {!loading && isMobile
          ? <ExpansionBadge style={{ marginLeft: 'auto', flexShrink: 0 }}>{label}</ExpansionBadge>
          : <RandomNote>scelta casuale della classifica</RandomNote>
        }
        {!loading && user && (quizAvailable || quizCountdown) && (
          <MiniQuizBtn
            $active={quizAvailable}
            onClick={(e) => { e.stopPropagation(); if (quizAvailable) navigate('/carte/quiz'); }}
          >
            {quizAvailable ? 'Quiz !' : `Quiz tra ${quizCountdown}`}
          </MiniQuizBtn>
        )}
        {!loading && cardAccount && miniClaimLabel && (
          <MiniClaimBtn
            $active={canClaim}
            onClick={(e) => { e.stopPropagation(); if (canClaim) navigate('/carte'); }}
          >
            {miniClaimLabel}
          </MiniClaimBtn>
        )}
      </Header>

      {loading ? (
        <LoadingBody>
          {[1, 2, 3].map((i) => (
            <SkeletonEntry key={i}>
              <SkeletonBlock $w="28px" $h="28px" />
              <SkeletonBlock $w="80%" $h="13px" />
              <SkeletonBlock $w="50%" $h="10px" />
              <SkeletonBlock $w="60%" $h="20px" />
            </SkeletonEntry>
          ))}
        </LoadingBody>
      ) : (
        <Body $isMobile={isMobile}>
          {data!.podium.map((entry, i) => (
            <>
              {i > 0 && <Divider key={`div-${i}`} $isMobile={isMobile} />}
              <PodiumEntryItem key={entry.rank} entry={entry} />
            </>
          ))}
        </Body>
      )}
    </Wrapper>
  );
};

export default CardPodium;
